<style>
.isNotMobile {
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: 45px;
  height: fit-content;
}
@media (max-width: 550px) {
  .isMobile {
    display: grid;
    grid-template-columns: 1fr;
    grid-template-rows: 45px;
    grid-template-columns: unset;
  }
}
</style>

<script>
import { createEventDispatcher } from "svelte"
import { useQuery } from "@sveltestack/svelte-query"
import {
  addYears,
  startOfWeek,
  endOfWeek,
  addWeeks,
  startOfMonth,
  endOfMonth,
  addMonths,
  subDays,
  subMonths,
} from "date-fns"
import { toDate } from "date-fns-tz"

const dispatch = createEventDispatcher()

import API from "@local/utils/api"
import Select from "@local/components/form/Select.svelte"
import Icon from "@local/components/images/Icon.svelte"

const modes = [
  {
    value: "thisWeek",
    label: "This Week's Capacity",
    startsAtFn: () => startOfWeek(toDateOnTz(new Date())),
    endsAtFn: (startsAt) => endOfWeek(startsAt),
  },
  {
    value: "nextWeek",
    label: "Next Week's Capacity",
    startsAtFn: () => startOfWeek(addWeeks(toDateOnTz(new Date()), 1)),
    endsAtFn: (startsAt) => endOfWeek(startsAt),
  },
  {
    value: "thisMonth",
    label: "This Month's Capacity",
    startsAtFn: () => startOfMonth(toDateOnTz(new Date())),
    endsAtFn: (startsAt) => endOfMonth(startsAt),
  },
  {
    value: "nextMonth",
    label: "Next Month's Capacity",
    startsAtFn: () => startOfMonth(addMonths(toDateOnTz(new Date()), 1)),
    endsAtFn: (startsAt) => endOfMonth(startsAt),
  },
  {
    value: "future",
    label: "Future Capacity",
    startsAtFn: () => toDateOnTz(new Date()),
    endsAtFn: (startsAt) => toDateOnTz(addYears(startsAt, 5)),
  },
  {
    value: "lastWeek",
    label: "Last Week's Capacity",
    startsAtFn: () => startOfWeek(subDays(toDateOnTz(new Date()), 7)),
    endsAtFn: (startsAt) => endOfWeek(startsAt),
  },
  {
    value: "lastMonth",
    label: "Last Month's Capacity",
    startsAtFn: () => startOfMonth(subMonths(toDateOnTz(new Date()), 1)),
    endsAtFn: (startsAt) => endOfMonth(startsAt),
  },
]

let mode = "thisWeek"
$: queryOptions = {
  queryKey: ["schedule-capacity", mode],
  queryFn: () => fetch(mode),
  refetchOnWindowFocus: false,
  refetchInterval: 5000,
}
const query = useQuery(queryOptions)
$: query.setOptions(queryOptions)
$: scheduleCapacity = fillCapacity($query.data)

export let timeZone
export let scheduleTags = []

function toDateOnTz(date) {
  return toDate(date, { timeZone })
}

function getStyle(tags) {
  if (tags[0] === "All") {
    return `background-color: rgba(63, 74, 89, var(--tw-bg-opacity)); color: white;font-size:14px;`
  }

  const tagDef = scheduleTags.find((tagDef) => tagDef.label === tags[0])
  return `background-color: ${tagDef.backgroundColor}; color: ${tagDef.textColor};font-size:14px;`
}

function fillCapacity(capacity = []) {
  let newCapacity = [...capacity]

  const existingTagLabels = capacity.map((capacity) => capacity.tags[0])
  const missingTags = scheduleTags
    .filter((tagDef) => !existingTagLabels.includes(tagDef.label))
    .map((tagDef) => tagDef.label)
  const emptyCapacity = missingTags.map((x) => ({
    tags: [x],
    usedCount: 0,
    totalCount: 0,
  }))

  const all = {
    tags: ["All"],
    usedCount: capacity.reduce((acc, { usedCount }) => acc + usedCount, 0),
    totalCount: capacity.reduce((acc, { totalCount }) => acc + totalCount, 0),
  }

  newCapacity = [...newCapacity, ...emptyCapacity]
    .filter((item) => item.tags[0] !== "All")
    .sort((a, b) => {
      if (a.totalCount !== b.totalCount) {
        return b.totalCount - a.totalCount
      }

      const aTag = a.tags[0]
      const bTag = b.tags[0]
      return aTag > bTag ? 1 : aTag < bTag ? -1 : 0
    })

  return [all, ...newCapacity]
}

async function fetch(mode) {
  const { startsAtFn, endsAtFn } = modes.find((x) => x.value === mode)
  const startsAt = startsAtFn()
  const endsAt = endsAtFn(startsAt)
  const searchParams = { timeZone, startsAt, endsAt }
  return await API.get(`admin/schedule-capacity`, { searchParams }).json()
}
</script>

<div
  class="flex justify-start gap-4 mb-4 pb-4 border-b border-gray-300 isMobile isNotMobile"
>
  <Select class="w-48" bind:value="{mode}" options="{modes}" />
  <div class="flex flex-wrap gap-3">
    {#each scheduleCapacity as { tags, usedCount, totalCount }}
      {#if !(usedCount === 0 && totalCount === 0)}
        <div
          class="flex flex-col bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden w-48"
        >
          <div
            class="flex items-center px-3 py-2 cursor-pointer w-48 h-8"
            style="{getStyle(tags)}"
          >
            <div
              class="flex flex-1 justify-center font-semibold"
              on:click="{() =>
                dispatch('tagClick', tags[0] === 'All' ? true : tags[0])}"
            >
              {tags[0]}
            </div>
            {#if tags[0] !== "All"}
              <div
                class="opacity-60 hover:opacity-100 ml-2"
                on:click="{() => dispatch('tagInfo', tags[0])}"
              >
                <Icon size="lg">info</Icon>
              </div>
            {/if}
          </div>

          <div class="px-2 py-2 bg-white flex items-center gap-2 h-6">
            {#if totalCount > 0}
              <div class="flex-1 bg-gray-200 rounded-full h-1.5">
                <div
                  class="h-1.5 rounded-full"
                  class:bg-red-700="{usedCount === totalCount}"
                  class:bg-yellow-500="{usedCount !== totalCount &&
                    usedCount / totalCount > 0.4}"
                  class:bg-green-700="{usedCount !== totalCount &&
                    usedCount / totalCount <= 0.4}"
                  style="width: {(usedCount / totalCount) * 100}%"
                ></div>
              </div>
            {/if}
            <div
              class="text-sm font-bold whitespace-nowrap"
              class:text-red-700="{usedCount === totalCount}"
              class:text-yellow-500="{usedCount !== totalCount &&
                totalCount > 0 &&
                usedCount / totalCount > 0.4}"
              class:text-green-700="{usedCount !== totalCount &&
                totalCount > 0 &&
                usedCount / totalCount <= 0.4}"
            >
              <span class="font-medium">{usedCount}</span>
              <span class="text-gray-400 font-normal">/</span>
              <span class="font-medium">{totalCount}</span>
            </div>
          </div>
        </div>
      {/if}
    {/each}
  </div>
</div>
